import Experience, { ExperienceSource } from 'model/IExperience';
import { ParseReturn } from 'model/UrlParsing';
import { SortType, typeToParams } from 'modules/search/model/sorting';
import { Observable } from 'rxjs';
import { reqWithABTest } from 'utils/abTesting';
import { getExperimentByName } from 'utils/abTests';
import req from 'utils/req';

import { EXP_SEARCH_PAGE_SIZE } from '../../constants';
import slugify from '../../utils/slugify';

const experiencesApiUrl = `${URL_API_HOST}/api/v2/experience`;
const experiencesByHostApiUrl = `${URL_API_HOST}/api/v2/experience/account`;
const italianCitiesPricing = getExperimentByName('italianCitiesPricing');

export function getOne(experienceId) {
  return Observable.fromPromise(
    req(experiencesApiUrl + '/' + experienceId).get(),
  );
}

export function getByHost(hostId, filters = {}) {
  let url = `${experiencesByHostApiUrl}/${hostId}`;
  const query = Object.entries(filters)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  if (query) {
    url += `?${query}`;
  }
  return Observable.fromPromise(req(url).get());
}

export function fetchSearchWithOther(params: URLSearchParams) {
  const search = params.toString();
  const url = `${URL_API_HOST}/api/v1/experience/searchWithOthers?fieldset=reactsearch&${search}`;
  return reqWithABTest(req(url).get, italianCitiesPricing.id)();
}

export function searchWithOthers(
  filters: ParseReturn,
  sort: SortType,
  options: { pageSize?: number } = {},
) {
  const params = filters.activeFilters.reduce(
    (acc, curr) => {
      if (curr.value) {
        if (curr.uid === 'family-friendly') {
          acc.set('other.familyfriendly', 'true');
        } else {
          acc.set(
            curr.searchWithOthersQueryName || curr.queryName,
            decodeURIComponent(curr?.value ?? ''),
          );
        }
      } else if (curr.subOptions.length > 0) {
        // only one single activityType.activity is supported. When multiple are sent (we have a maximum of 2), don't send them at all.
        if (
          curr.uid === 'activityType.activity' &&
          curr.subOptions.length === 2
        ) {
          return acc;
        }

        curr.subOptions.forEach((subOptions) => {
          acc.append(
            curr.uid,
            decodeURIComponent(
              subOptions.queryValue || subOptions?.value || '',
            ),
          );
        });
      }
      return acc;
    },
    new URLSearchParams({ page: filters.page.toString() }),
  );

  if (filters.country) {
    params.set(
      'location.country',
      decodeURIComponent(filters.country.replace(/-/g, ' ')),
    );
  }

  if (filters.city) {
    params.set(
      'location.city',
      decodeURIComponent(filters.city.replace(/-/g, ' ')),
    );
  }

  if (filters.lang) {
    params.set('lang', filters.lang);
  }

  params.set('pagesize', `${options.pageSize || EXP_SEARCH_PAGE_SIZE}`);

  if (
    filters.activeFilters &&
    filters.activeFilters.some((f) => f.uid === 'date.from' && f?.value) &&
    !filters.activeFilters.some((f) => f.uid === 'date.to' && f?.value)
  ) {
    const dateTo = filters.activeFilters.find((f) => f.uid === 'date.from')
      ?.value;
    params.set('date.to', dateTo);
  }

  if (sort) {
    typeToParams[sort].forEach(({ name, value }) => params.set(name, value));
  }

  return fetchSearchWithOther(params);
}

export function fetchPopularExperiences(langCode) {
  return req(
    `${URL_API_HOST}/api/v3/experiences/top?include=experience&lang=${langCode}`,
  ).get();
}

export function fetchFamilyFriendlyExperiences(country, city, langCode) {
  return req(
    `${URL_API_HOST}/api/v3/experiences/list/family_friendly_${slugify(
      country,
    )}_${slugify(city)}?include=experience&per_page=3&lang=${langCode}`,
  ).get();
}

export function fetchExperience(
  experienceId,
  langCode,
  addLiveParam?: boolean,
): Promise<ExperienceSource> {
  const url = `${URL_API_HOST}/api/v3/experience/${experienceId}?lang=${langCode}${
    addLiveParam ? '&live=true' : ''
  }`;
  return reqWithABTest(req(url).get, italianCitiesPricing.id)();
}

export function fetchAvailableTimes(
  experienceId: string,
  date: string,
  hostId?: string,
) {
  if (hostId) {
    return req(
      `${URL_API_HOST}/api/v2/agenda/experience/times/${experienceId}/${date}/${hostId}`,
    ).get();
  }
  return req(
    `${URL_API_HOST}/api/v2/agenda/experience/times/${experienceId}/${date}`,
  ).get();
}

export function fetchHostExperiences(hostId) {
  return req(`${URL_API_HOST}/api/v1/experience/host/${hostId}`).get();
}

export interface AvailabilityPerHost {
  availability: {
    [hostId: string]: string[];
  };
}

export function fetchAvailableDatesPerHost(experienceId) {
  return req(
    `${URL_API_HOST}/api/v2/agenda/experience/availability-by-host/${experienceId}`,
  ).get() as Promise<AvailabilityPerHost>;
}

export function fetchHostExperiencesPaginated(
  prettyUrl,
): Promise<Experience[]> {
  return req(
    `${URL_API_HOST}/api/v1/host/experiences/${prettyUrl}?page=1&pageSize=30`,
  ).get();
}
